import { z } from 'zod';
import type { IsoDate } from './common.js';
import type { UserSummary } from './users.js';

/** What people can report to the Nexo team: something broken or an idea. */
export const feedbackKinds = ['bug', 'idea'] as const;
export type FeedbackKind = (typeof feedbackKinds)[number];

export const feedbackKindLabels: Record<FeedbackKind, string> = {
  bug: 'Error',
  idea: 'Mejora',
};

export const feedbackStatuses = ['new', 'in_progress', 'done', 'dismissed'] as const;
export type FeedbackStatus = (typeof feedbackStatuses)[number];

export const feedbackStatusLabels: Record<FeedbackStatus, string> = {
  new: 'Recibido',
  in_progress: 'En curso',
  done: 'Resuelto',
  dismissed: 'Descartado',
};

/** Where the report was sent from, filled in by the app (helps reproduce errors). */
export const feedbackContextSchema = z.object({
  appVersion: z.string().max(40).optional(),
  /** `web` or `desktop`. */
  platform: z.string().max(20).optional(),
  /** Path of the screen the person was on. */
  path: z.string().max(500).optional(),
  userAgent: z.string().max(500).optional(),
});
export type FeedbackContext = z.infer<typeof feedbackContextSchema>;

export const createFeedbackSchema = z.object({
  kind: z.enum(feedbackKinds),
  title: z.string().trim().min(3, 'Mínimo 3 caracteres').max(120),
  description: z.string().trim().min(1, 'Cuéntanos un poco más').max(5000),
  context: feedbackContextSchema.default({}),
});
export type CreateFeedbackInput = z.input<typeof createFeedbackSchema>;

export const updateFeedbackSchema = z
  .object({
    status: z.enum(feedbackStatuses),
    /** Reply for the person who reported it (they see it in their profile). */
    reply: z.string().trim().max(2000).nullable(),
  })
  .partial();
export type UpdateFeedbackInput = z.infer<typeof updateFeedbackSchema>;

export const adminFeedbackQuerySchema = z.object({
  status: z.enum(feedbackStatuses).optional(),
  kind: z.enum(feedbackKinds).optional(),
  page: z.coerce.number().int().min(1).default(1),
});
export type AdminFeedbackQuery = {
  status?: FeedbackStatus | undefined;
  kind?: FeedbackKind | undefined;
  page?: number;
};

export interface Feedback {
  id: string;
  kind: FeedbackKind;
  title: string;
  description: string;
  status: FeedbackStatus;
  reply: string | null;
  context: FeedbackContext;
  /** Null if the account was deleted. */
  author: (UserSummary & { email: string }) | null;
  createdAt: IsoDate;
  updatedAt: IsoDate;
}
